import React, { useState } from 'react'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import SEOHead from '../SEOHead'
import { 
  Image, 
  UserCheck, 
  MessageSquare, 
  LogOut, 
  Menu, 
  X,
  Search,
  User,
  Users,
  LayoutDashboard,
  Building2,
  Calendar
} from 'lucide-react'

const AdminLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  const menuItems = [
    {
      name: 'Dashboard',
      path: '/admin/dashboard',
      icon: LayoutDashboard,
      description: 'Overview'
    },
    {
      name: 'Hero Slider',
      path: '/admin/slider',
      icon: Image,
      description: 'Homepage Slider'
    },
    {
      name: 'Gallery',
      path: '/admin/gallery',
      icon: Image,
      description: 'Media Management'
    },
    {
      name: 'Management Team',
      path: '/admin/management',
      icon: Building2,
      description: 'School Management'
    },
    {
      name: 'Alumni Network',
      path: '/admin/alumni',
      icon: Users,
      description: 'Alumni Management'
    },
    {
      name: 'Admissions',
      path: '/admin/admissions',
      icon: UserCheck,
      description: 'Admission Requests'
    },
    {
      name: 'Important Dates',
      path: '/admin/important-dates',
      icon: Calendar,
      description: 'Admission Dates'
    },
    {
      name: 'Messages',
      path: '/admin/messages',
      icon: MessageSquare,
      description: 'Contact Messages'
    }
  ]

  const handleLogout = () => {
    logout()
    navigate('/admin/login')
  }

  const isActivePath = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/')
  }

  const canonicalAdminUrl = `https://vdpms.in${location.pathname}`

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <SEOHead
        title="Admin Area | Shri V.D. Patel Madhyamik School"
        description="Restricted administrative workspace for managing Shri V.D. Patel Madhyamik School content."
        canonical={canonicalAdminUrl}
        noIndex
      />
      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-xl transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="flex items-center justify-between h-16 px-6 border-b border-gray-200">
          <div className="flex items-center">
            <img src="/logo.jpeg" alt="SPET Logo" className="h-8 w-8 rounded-full" />
            <span className="ml-2 text-lg font-bold text-gray-900">SPET Admin</span>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden text-gray-500 hover:text-gray-700 p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200 min-w-[44px] min-h-[44px] flex items-center justify-center"
            aria-label="Close navigation menu"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <nav className="mt-6 px-3">
          <div className="space-y-1">
            {menuItems.map((item) => {
              const Icon = item.icon
              const isActive = isActivePath(item.path)
              
              return (
                <button
                  key={item.name}
                  onClick={() => {
                    navigate(item.path)
                    setSidebarOpen(false)
                  }}
                  className={`w-full flex items-center px-3 py-3 text-sm font-medium rounded-lg transition-all duration-200 group min-h-[48px] ${
                    isActive
                      ? 'bg-primary-purple text-white shadow-lg transform scale-[1.02]'
                      : 'text-gray-700 hover:bg-gray-100 hover:text-primary-purple hover:scale-[1.01]'
                  }`}
                  type="button"
                  aria-current={isActive ? 'page' : undefined}
                >
                  <Icon className={`w-5 h-5 mr-3 ${isActive ? 'text-white' : 'text-gray-400 group-hover:text-primary-purple'}`} />
                  <div className="text-left">
                    <div className={isActive ? 'text-white' : 'text-gray-900'}>{item.name}</div>
                    <div className={`text-xs ${isActive ? 'text-purple-100' : 'text-gray-500'}`}>
                      {item.description}
                    </div>
                  </div>
                </button>
              )
            })}
          </div>
        </nav>

        {/* Logout */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-200">
          <button
            onClick={handleLogout}
            className="w-full flex items-center px-3 py-3 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200 min-h-[48px] hover:text-red-700"
            type="button"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col lg:ml-0">
        {/* Top Header */}
        <header className="bg-white shadow-sm border-b border-gray-200 h-16 flex items-center justify-between px-4 lg:px-6">
          <div className="flex items-center">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden text-gray-500 hover:text-gray-700 mr-4 p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200 min-w-[44px] min-h-[44px] flex items-center justify-center"
              aria-label="Open navigation menu"
            >
              <Menu className="w-6 h-6" />
            </button>
            
            <div className="relative hidden sm:block">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="search"
                placeholder="Search..."
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-primary-purple focus:border-primary-purple transition-colors duration-200"
              />
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-4">
            {/* Mobile Search Button */}
            <button className="sm:hidden text-gray-500 hover:text-gray-700 p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200 min-w-[44px] min-h-[44px] flex items-center justify-center">
              <Search className="w-5 h-5" />
            </button>
            
            <div className="flex items-center gap-2">
              <div className="bg-primary-purple text-white rounded-full p-1.5 min-w-[32px] min-h-[32px] flex items-center justify-center">
                <User className="w-4 h-4" />
              </div>
              <div className="hidden sm:block">
                <p className="text-sm font-medium text-gray-700 leading-tight">{user?.name}</p>
                <p className="text-xs text-gray-500 leading-tight">{user?.role || 'Administrator'}</p>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main id="main-content" className="flex-1 overflow-auto p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  )
}

export default AdminLayout